import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import {
    getAdminUsers,
    createAdminUser,
    updateAdminUser,
    setAdminUserActive,
    resetAdminUserPassword,
} from '../../api/adminUsers';

/**
 * 사용자 관리 — owner 전용
 *
 * 계정을 **지우지 않는다.** 비활성화만 한다. 작성자·처리자 기록이 계정 id 로 남기 때문에
 * 지우면 과거 기록의 주체를 알 수 없게 된다. 서버도 DELETE 엔드포인트를 두지 않았다.
 */
const ROLE_LABEL = { owner: '소유자', staff: '일반' };

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [denied, setDenied] = useState(false);

    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState({ username: '', password: '', name: '', email: '', role: 'staff' });
    const [editId, setEditId] = useState(null);
    const [edit, setEdit] = useState({ name: '', email: '', role: 'staff' });
    const [pwFor, setPwFor] = useState(null);
    const [newPw, setNewPw] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            setUsers(await getAdminUsers());
            setDenied(false);
        } catch (e) {
            setError(e.message);
            if (/소유자|owner|권한/.test(e.message)) setDenied(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const run = async (fn, successMessage) => {
        setError(''); setNotice('');
        try {
            const result = await fn();
            setNotice(successMessage ?? result?.message ?? '완료했습니다.');
            await load();
            return true;
        } catch (e) {
            setError(e.message);
            return false;
        }
    };

    const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';
    const btn = 'px-3 py-1.5 rounded-lg text-xs font-bold transition-colors';

    return (
        <AdminLayout>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">사용자 관리</h2>
                    <p className="text-sm text-gray-500 mt-1">관리자 계정을 만들고 권한과 사용 여부를 정합니다.</p>
                </div>
                {!denied && (
                    <button
                        type="button"
                        onClick={() => { setCreating(true); setDraft({ username: '', password: '', name: '', email: '', role: 'staff' }); }}
                        disabled={creating}
                        className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold disabled:opacity-40"
                    >
                        + 계정 추가
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 whitespace-pre-line">{error}</div>
            )}
            {notice && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">{notice}</div>
            )}

            {denied ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
                    <p className="text-sm text-gray-600">이 화면은 소유자 권한이 있는 계정만 볼 수 있습니다.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs font-bold text-gray-500 bg-gray-50 border-b border-gray-200">
                                <th className="px-4 py-3">아이디</th>
                                <th className="px-4 py-3">이름</th>
                                <th className="px-4 py-3">이메일</th>
                                <th className="px-4 py-3 w-24">권한</th>
                                <th className="px-4 py-3 w-24">상태</th>
                                <th className="px-4 py-3 w-40">마지막 로그인</th>
                                <th className="px-4 py-3 w-48 text-right">관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {creating && (
                                <tr className="border-b border-gray-100 bg-primary-50/30">
                                    <td className="px-4 py-3"><input className={inputCls} value={draft.username} placeholder="아이디" onChange={(e) => setDraft({ ...draft, username: e.target.value })} /></td>
                                    <td className="px-4 py-3"><input className={inputCls} value={draft.name} placeholder="이름" onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></td>
                                    <td className="px-4 py-3"><input className={inputCls} value={draft.email} placeholder="이메일 (선택)" onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></td>
                                    <td className="px-4 py-3">
                                        <select className={inputCls} value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
                                            <option value="staff">일반</option>
                                            <option value="owner">소유자</option>
                                        </select>
                                    </td>
                                    <td className="px-4 py-3" colSpan={2}>
                                        <input className={inputCls} type="password" autoComplete="new-password" value={draft.password} placeholder="초기 비밀번호 (10자 이상)" onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button type="button" className={`${btn} bg-primary-500 text-white mr-1`} onClick={async () => { if (await run(() => createAdminUser(draft), '계정을 만들었습니다.')) setCreating(false); }}>추가</button>
                                        <button type="button" className={`${btn} bg-gray-100 text-gray-700`} onClick={() => setCreating(false)}>취소</button>
                                    </td>
                                </tr>
                            )}

                            {loading ? (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">불러오는 중…</td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">계정이 없습니다.</td></tr>
                            ) : users.map((u) => (
                                <React.Fragment key={u.id}>
                                    <tr className={`border-b border-gray-100 ${u.isActive ? '' : 'bg-gray-50 text-gray-400'}`}>
                                        {editId === u.id ? (
                                            <>
                                                <td className="px-4 py-3 font-mono text-xs">{u.username}</td>
                                                <td className="px-4 py-3"><input className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></td>
                                                <td className="px-4 py-3"><input className={inputCls} value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></td>
                                                <td className="px-4 py-3">
                                                    <select className={inputCls} value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}>
                                                        <option value="staff">일반</option>
                                                        <option value="owner">소유자</option>
                                                    </select>
                                                </td>
                                                <td className="px-4 py-3" colSpan={2}></td>
                                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                                    <button type="button" className={`${btn} bg-primary-500 text-white mr-1`} onClick={async () => { if (await run(() => updateAdminUser(u.id, edit), '수정했습니다.')) setEditId(null); }}>저장</button>
                                                    <button type="button" className={`${btn} bg-gray-100 text-gray-700`} onClick={() => setEditId(null)}>취소</button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-4 py-3 font-mono text-xs">{u.username}</td>
                                                <td className="px-4 py-3 font-semibold text-gray-900">{u.name || '—'}</td>
                                                <td className="px-4 py-3 text-gray-600">{u.email || '—'}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${u.role === 'owner' ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-600'}`}>
                                                        {ROLE_LABEL[u.role] ?? u.role}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${u.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                                                        {u.isActive ? '사용' : '중지'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-xs text-gray-500">
                                                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('ko-KR') : '기록 없음'}
                                                </td>
                                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                                    <button type="button" className={`${btn} text-gray-700 hover:text-primary-500`} onClick={() => { setEditId(u.id); setEdit({ name: u.name || '', email: u.email || '', role: u.role }); }}>수정</button>
                                                    <button type="button" className={`${btn} text-gray-700 hover:text-primary-500`} onClick={() => { setPwFor(pwFor === u.id ? null : u.id); setNewPw(''); }}>비밀번호</button>
                                                    <button type="button" className={`${btn} ${u.isActive ? 'text-red-600 hover:text-red-700' : 'text-emerald-600 hover:text-emerald-700'}`}
                                                        onClick={() => run(() => setAdminUserActive(u.id, !u.isActive), u.isActive ? '계정을 중지했습니다.' : '계정을 다시 사용합니다.')}>
                                                        {u.isActive ? '중지' : '사용'}
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                    </tr>

                                    {pwFor === u.id && (
                                        <tr className="border-b border-gray-100 bg-amber-50/40">
                                            <td colSpan={7} className="px-4 py-3">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-xs font-bold text-gray-700">{u.username} 비밀번호 재설정</span>
                                                    <input className={`${inputCls} max-w-xs`} type="password" autoComplete="new-password" value={newPw} placeholder="새 비밀번호 (10자 이상)" onChange={(e) => setNewPw(e.target.value)} />
                                                    <button type="button" className={`${btn} bg-primary-500 text-white`} onClick={async () => { if (await run(() => resetAdminUserPassword(u.id, newPw))) { setPwFor(null); setNewPw(''); } }}>재설정</button>
                                                    <button type="button" className={`${btn} bg-white border border-gray-300 text-gray-700`} onClick={() => setPwFor(null)}>취소</button>
                                                    <span className="text-[11px] text-gray-500">현재 비밀번호를 묻지 않습니다. 본인에게 새 비밀번호를 직접 전달하세요.</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="mt-4 text-xs text-gray-500">
                계정은 삭제하지 않고 &lsquo;중지&rsquo;합니다. 작성자·처리자 기록이 계정에 연결되어 있어, 지우면 과거 기록의 주체를 알 수 없게 됩니다.
            </p>
        </AdminLayout>
    );
};

export default UserManagement;
